import React, { useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { LocationDropdown } from "./LocationDropdown";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import BannerCounter from "../../../../core/common/banner-counter/counter";
import './flightSearchForm.scss';

const FlightSearchForm: React.FC = () => {
    const navigate = useNavigate();

    const [flightRadio, setFlightRadio] = useState<string>("oneway");
    const [flightSearchDetails, setFlightSearchDetails] = useState({
        from: { name: "Newyork", country: "USA", iataCode: "NYC" },
        to: { name: "Boston", country: "USA", iataCode: "BOS" },
        departureDate: dayjs(),
        returnDate: dayjs().add(1, "day"),
        passengers: 1,
        cabinClass: "Economy",
        multiTripDetails: [
            {
                from: { name: "Newyork", country: "USA", iataCode: "NYC" },
                to: { name: "Boston", country: "USA", iataCode: "BOS" },
                departureDate: dayjs(),
            },
        ],
    });

    const [passengers, setPassengers] = useState({
        adults: 1,
        children: 0,
        infants: 0,
    });

    const handleInputChange = (field: string, value: any) => {
        setFlightSearchDetails((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSwapCities = () => {
        setFlightSearchDetails((prev) => ({
            ...prev,
            from: prev.to,
            to: prev.from,
        }));
    };

    const handleMultiCityChange = (
        index: number,
        field: string,
        value: any
    ) => {
        setFlightSearchDetails((prev) => {
            const updatedMultiTripDetails = [...prev.multiTripDetails];
            updatedMultiTripDetails[index] = {
                ...updatedMultiTripDetails[index],
                [field]: value,
            };
            return {
                ...prev,
                multiTripDetails: updatedMultiTripDetails,
            };
        });
    };

    const addMultiCityTrip = () => {
        setFlightSearchDetails((prev) => ({
            ...prev,
            multiTripDetails: [
                ...prev.multiTripDetails,
                {
                    from: { name: "", country: "", iataCode: "" },
                    to: { name: "", country: "", iataCode: "" },
                    departureDate: dayjs(),
                },
            ],
        }));
    };

    const removeMultiCityTrip = (index: number) => {
        setFlightSearchDetails((prev) => {
            const updatedMultiTripDetails = [...prev.multiTripDetails];
            updatedMultiTripDetails.splice(index, 1);
            return {
                ...prev,
                multiTripDetails: updatedMultiTripDetails,
            };
        });
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!flightSearchDetails) return; // safety check

        // Safely convert Dayjs dates to ISO strings
        const enrichedDetails = {
            ...flightSearchDetails,
            departureDate: flightSearchDetails.departureDate
                ? flightSearchDetails.departureDate.toISOString()
                : null,
            returnDate: flightSearchDetails.returnDate
                ? flightSearchDetails.returnDate.toISOString()
                : null,
            multiTripDetails: Array.isArray(flightSearchDetails.multiTripDetails)
                ? flightSearchDetails.multiTripDetails.map((trip) => ({
                    ...trip,
                    departureDate: trip.departureDate
                        ? trip.departureDate.toISOString()
                        : null,
                }))
                : [],
            passengers: passengers || { adults: 1, children: 0, infants: 0 }, // safety fallback
        };

        // Navigate to flight list page with state
        navigate(all_routes.flightList, {
            state: {
                flightSearchDetails: enrichedDetails,
                flightType: flightRadio || "oneway",
            },
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            {/* --- Trip Type Radio Buttons --- */}
            <div className="d-flex align-items-center flex-wrap gap-3 mb-3">
                {["oneway", "roundtrip", "multiway"].map((type) => (
                    <div className="form-check d-flex align-items-center" key={type}>
                        <input
                            className="form-check-input mt-0"
                            type="radio"
                            id={type}
                            name="tripType"
                            checked={flightRadio === type}
                            onChange={() => setFlightRadio(type)}
                        />
                        <label
                            className="form-check-label fs-14 ms-2 text-capitalize"
                            htmlFor={type}
                        >
                            {type === "multiway" ? "Multi City" : type}
                        </label>
                    </div>
                ))}
            </div>

            {/* --- Multi-City Layout --- */}
            {flightRadio === "multiway" ? (
                <div className="multi-city-section">
                    {flightSearchDetails.multiTripDetails.map((trip, index) => (
                        <div
                            className="d-flex flex-column flex-lg-row align-items-lg-end gap-3 mb-3 border-bottom pb-3"
                            key={index}
                        >
                            <div className="flex-grow-1 d-flex flex-column flex-md-row gap-3">
                                {/* From */}
                                <div
                                    className="form-item flex-fill"
                                    style={{ minWidth: "220px", height: "100%" }}
                                >
                                    <LocationDropdown
                                        label="From"
                                        defaultCity={trip.from.name}
                                        defaultAirport={trip.from.country}
                                        onChange={(value: any) =>
                                            handleMultiCityChange(index, "from", value)
                                        }
                                    />
                                </div>

                                {/* To */}
                                <div
                                    className="form-item flex-fill"
                                    style={{ minWidth: "220px", height: "100%" }}
                                >
                                    <LocationDropdown
                                        label="To"
                                        defaultCity={trip.to.name}
                                        defaultAirport={trip.to.country}
                                        onChange={(value: any) =>
                                            handleMultiCityChange(index, "to", value)
                                        }
                                    />
                                </div>

                                {/* Departure */}
                                <div
                                    className="form-item flex-fill"
                                    style={{
                                        minWidth: "200px",
                                        maxWidth: "240px",
                                        height: "100%",
                                    }}
                                >
                                    <label className="form-label fs-14 text-default mb-1">
                                        Departure
                                    </label>
                                    <DatePicker
                                        className="form-control datetimepicker uniform-field"
                                        placeholder="dd/mm/yyyy"
                                        value={trip.departureDate}
                                        format="DD-MM-YYYY"
                                        onChange={(date) =>
                                            handleMultiCityChange(index, "departureDate", date)
                                        }
                                    />
                                </div>
                            </div>

                            {/* Remove button area - fixed width */}
                            <div
                                className="text-lg-end d-flex align-items-end"
                                style={{ width: "140px", minWidth: "140px" }}
                            >
                                {index > 0 ? (
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger w-100 rounded-pill uniform-field"
                                        onClick={() => removeMultiCityTrip(index)}
                                    >
                                        <i className="fa fa-times me-1"></i> Remove
                                    </button>
                                ) : (
                                    <div style={{ height: "44px" }}></div>
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Add / Search Row */}
                    <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
                        <button
                            type="button"
                            className="btn btn-outline-primary rounded-pill px-4 py-2"
                            onClick={addMultiCityTrip}
                        >
                            <i className="fa fa-plus me-2"></i>Add Another City
                        </button>

                        <button
                            type="submit"
                            className="btn btn-primary rounded-pill px-5 py-2 fw-semibold"
                        >
                            Search Flights
                        </button>
                    </div>
                </div>
            ) : (
                /* Oneway / Roundtrip logic unchanged */
                <div className="d-lg-flex">
                    <div className="d-flex form-info">
                        <LocationDropdown
                            label="From"
                            defaultCity={flightSearchDetails.from.name}
                            defaultAirport={flightSearchDetails.from.country}
                            onChange={(value: any) => handleInputChange("from", value)}
                        />
                        <LocationDropdown
                            label="To"
                            defaultCity={flightSearchDetails.to.name}
                            defaultAirport={flightSearchDetails.to.country}
                            isSwappable={true}
                            onSwap={handleSwapCities}
                            onChange={(value: any) => handleInputChange("to", value)}
                        />
                        <div className="form-item">
                            <label className="form-label fs-14 text-default mb-1">
                                Departure
                            </label>
                            <DatePicker
                                className="form-control datetimepicker uniform-field"
                                placeholder="dd/mm/yyyy"
                                value={flightSearchDetails.departureDate}
                                format="DD-MM-YYYY"
                                onChange={(date) => handleInputChange("departureDate", date)}
                            />
                        </div>
                        {flightRadio === "roundtrip" && (
                            <div className="form-item">
                                <label className="form-label fs-14 text-default mb-1">
                                    Return
                                </label>
                                <DatePicker
                                    className="form-control datetimepicker uniform-field"
                                    placeholder="dd/mm/yyyy"
                                    value={flightSearchDetails.returnDate}
                                    format="DD-MM-YYYY"
                                    onChange={(date) => handleInputChange("returnDate", date)}
                                />
                            </div>
                        )}

                        <div className="form-item dropdown">
                            <div
                                data-bs-toggle="dropdown"
                                data-bs-auto-close="outside"
                                role="menu"
                            >
                                <label className="form-label fs-14 text-default mb-1">
                                    Travellers and cabin class
                                </label>
                                <h5>
                                    {passengers.adults + passengers.children + passengers.infants}{" "}
                                    <span className="fw-normal fs-14">Persons</span>
                                </h5>
                                <p className="fs-12 mb-0">
                                    {passengers.adults} Adult, {passengers.children} Child,{" "}
                                    {passengers.infants} Infant, {flightSearchDetails.cabinClass}
                                </p>
                            </div>
                            <div className="dropdown-menu dropdown-menu-end dropdown-xl">
                                <h5 className="mb-3">Select Travelers &amp; Class</h5>
                                <div className="mb-3 border br-10 info-item pb-1">
                                    <h6 className="fs-16 fw-medium mb-2">Travellers</h6>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label className="form-label text-gray-9 mb-2">
                                                    Adults (12+)
                                                </label>
                                                <BannerCounter
                                                    value={passengers.adults}
                                                    onChange={(value) =>
                                                        setPassengers((p) => ({ ...p, adults: value }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label className="form-label text-gray-9 mb-2">
                                                    Children (2-12)
                                                </label>
                                                <BannerCounter
                                                    value={passengers.children}
                                                    onChange={(value) =>
                                                        setPassengers((p) => ({ ...p, children: value }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label className="form-label text-gray-9 mb-2">
                                                    Infants (&lt;2)
                                                </label>
                                                <BannerCounter
                                                    value={passengers.infants}
                                                    onChange={(value) =>
                                                        setPassengers((p) => ({ ...p, infants: value }))
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-3 border br-10 info-item pb-1">
                                    <h6 className="fs-16 fw-medium mb-2">Cabin Class</h6>
                                    <div className="d-flex align-items-center flex-wrap">
                                        <div className="form-check me-3 mb-3">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                value="Any"
                                                name="cabin-class"
                                                id="any"
                                                checked={flightSearchDetails.cabinClass === "Any"}
                                                onChange={(e) =>
                                                    handleInputChange("cabinClass", e.target.value)
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="any"
                                            >
                                                Any Class
                                            </label>
                                        </div>
                                        <div className="form-check me-3 mb-3">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                value="Economy"
                                                name="cabin-class"
                                                id="economy"
                                                checked={flightSearchDetails.cabinClass === "Economy"}
                                                onChange={(e) =>
                                                    handleInputChange("cabinClass", e.target.value)
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="economy"
                                            >
                                                Economy
                                            </label>
                                        </div>
                                        <div className="form-check me-3 mb-3">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                value="Premium Economy"
                                                name="cabin-class"
                                                id="premium-economy"
                                                checked={flightSearchDetails.cabinClass === "Premium Economy"}
                                                onChange={(e) =>
                                                    handleInputChange("cabinClass", e.target.value)
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="premium-economy"
                                            >
                                                Premium Economy
                                            </label>
                                        </div>
                                        <div className="form-check me-3 mb-3">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                value="Business"
                                                name="cabin-class"
                                                id="business"
                                                checked={flightSearchDetails.cabinClass === "Business"}
                                                onChange={(e) =>
                                                    handleInputChange("cabinClass", e.target.value)
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="business"
                                            >
                                                Business
                                            </label>
                                        </div>
                                        <div className="form-check me-3 mb-3">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                value="Premium Business"
                                                name="cabin-class"
                                                id="premium-business"
                                                checked={flightSearchDetails.cabinClass === "Premium Business"}
                                                onChange={(e) =>
                                                    handleInputChange("cabinClass", e.target.value)
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="premium-business"
                                            >
                                                Premium Business
                                            </label>
                                        </div>
                                        <div className="form-check me-3 mb-3">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                value="First"
                                                name="cabin-class"
                                                id="first"
                                                checked={flightSearchDetails.cabinClass === "First"}
                                                onChange={(e) =>
                                                    handleInputChange("cabinClass", e.target.value)
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="first"
                                            >
                                                First Class
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary search-btn rounded"
                        >
                            Search
                        </button>
                    </div>
                </div>
            )}
        </form>
    );
};

export default FlightSearchForm;
